import { Location } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { NotificationHandlerComponent } from '@ebootis/utilities';
import { ConfigurationService } from 'projects/zbs-app/src/app/components/configuration-select/configuration.service';
import { ErrorCodesI18n } from 'projects/zbs-app/src/app/i18n/ErrorCodesI18n';
import { ArticleDetailsByOid } from 'projects/zbs-app/src/app/services/Ebootis.Queries/ArticleDetailsByOid';
import { ArticleLobOids } from 'projects/zbs-app/src/app/services/Ebootis.Queries/ArticleLobOids';
import { LobByOid } from 'projects/zbs-app/src/app/services/Ebootis.Queries/LobByOid';
import { ArticleBasketService } from 'projects/zbs-app/src/app/services/article-basket.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ArticleService } from '../../article.service';
import { ArticleDetailI18n } from './ArticleDetailI18n';

export interface ArticleImageDialogData {
	oid: string;
}

@Component({
	selector: 'app-article-detail',
	templateUrl: './article-detail.component.html',
	styleUrls: ['./article-detail.component.scss'],
	providers: [ArticleDetailI18n, ErrorCodesI18n],
})
export class ArticleDetailComponent implements OnDestroy, OnInit {
	public readonly QTTY: string = 'qtty';
	@Input() set articleOid(p_articleOid: string) {
		this.m_articleOid = p_articleOid;
		this.getArticleLobOids(this.m_articleOid);
		this.m_articleDetails = {};
		this.m_imageArray = [];
		this.m_articleService.getArticleDetailsByOid(this.m_articleOid).pipe(
			takeUntil(this.m_subscriptionsSubject),
		).subscribe({
			next: (res) => {
				this.m_articleDetails = res;

				if (this.m_articleDetails.images) {
					if (this.m_articleDetails.images.length > 0) {
						this.m_imageArray = this.m_articleDetails.images;
						this.m_activeImage = this.m_imageArray[0].oid;
					}
				}
				if (this.m_articleDetails.primaryImage) {
					this.m_imageArray.unshift({ oid: this.m_articleDetails.primaryImage });
					this.m_activeImage = this.m_articleDetails.primaryImage;
				}

				const validators = this.m_basketService.getQuantityValidators(this.m_articleDetails);
				this.m_articleDetailForm.get(this.QTTY).setValidators(validators);
				if (this.m_articleDetails.salesAmountMin) {
					this.m_articleDetailForm.get(this.QTTY).patchValue(this.m_articleDetails.salesAmountMin);
				} else {
					this.m_articleDetailForm.get(this.QTTY).patchValue(1);
				}
				this.m_articleDetailForm.get(this.QTTY).markAsTouched();

				// Initialisieren des Formulars für Variantendefinitionen
				this.initVariantDefForm(res);

				const variantOid = this.m_route.snapshot.queryParams.variant;
				if (variantOid)
					this.selectVariantByOid(variantOid);
				else {
					// Wählt die erste verfügbare Variante aus, falls vorhanden
					if (this.m_articleDetails?.variantDef?.variants?.length > 0)
						this.selectVariant(this.m_articleDetails?.variantDef?.variants[0]);
				}
			}
		});
	}
	@Input() isVariant: boolean;

	@Input() set isEmbedded(p_embedded: boolean) {
		this.m_embedded = p_embedded;
	}

	@Input() set onlyOrderableArticles(p_onlyOrderableArticles: boolean) {
		this.m_onlyOrderableArticles = p_onlyOrderableArticles;
	}


	@Output() public exit: EventEmitter<boolean> = new EventEmitter();
	@Output() public articleSelect: EventEmitter<string> = new EventEmitter();

	public m_onlyOrderableArticles: boolean = true;
	public m_embedded: boolean = false;

	private readonly ARTICLE_OID = 'articleOid';

	private m_articleOid: string = '';
	private m_subscriptionsSubject = new Subject();
	private m_selectedConfigOid: string;
	public m_articleLobs: ArticleLobOids.Result[] = [];
	public m_articleDetails: ArticleDetailsByOid.Result;
	public m_myImageSrc: SafeUrl;
	public m_imageIndex: number = 0;
	public m_onlyAvailableStock: boolean;
	public m_imageArray: ArticleDetailsByOid.Image[] = [];
	public m_activeImage: string;
	public m_articleDetailForm: UntypedFormGroup;
	public m_showProcPrice: boolean = false;
	public m_showStoragePackaging: boolean = false;
	public m_showSubPackaging: boolean = false;
	public m_showStockLevel: boolean = false;
	public m_showReservedStock: boolean = false;
	/** Rückstand anzeigen? */
	public m_showBackOrder: boolean = false;
	/** Mindestbestand anzeigen? */
	public m_showMinStock: boolean = false;
	/** VK-Preis anzeigen? */
	public m_showSalesPrice: boolean = false;
	/** verfügbaren Bestand anzeigen? */
	public m_showAvailableStock: boolean = false;
	/** Wieder verfügbar ab (WBZ) anzegen?*/
	public m_showReplacementTime: boolean = false;
	/** EAN anzeigen? */
	public m_showEAN: boolean = false;
	/** Kategorie anzeigen? */
	public m_showCategory: boolean = false;
	/** Gruppe anzeigen? */
	public m_showArticleGroup: boolean = false;
	/** Abteilung anzeigen? */
	public m_showDepartment: boolean = false;
	/** Info 1-6 anzeigen? */
	public m_showInfo: Map<string, boolean> = new Map<string, boolean>();
	public m_showInfoKeys: string[];

	/* Variablen für Artikelvarianten */
	public m_variantDefForm: UntypedFormGroup;
	public m_variantArticleOid: string;
	public m_variantClsAttributeMap: Map<string, string[]>;

	constructor(
		private m_articleService: ArticleService,
		public m_dialog: MatDialog,
		private m_route: ActivatedRoute,
		private m_basketService: ArticleBasketService,
		private m_snackBar: MatSnackBar,
		private m_translation: ArticleDetailI18n,
		private m_configurationService: ConfigurationService,
		private m_location: Location,
		private m_formBuilder: UntypedFormBuilder,
	) {
		this.m_articleDetailForm = this.m_formBuilder.group({
			[this.QTTY]: ['', Validators.required],
		});
	}

	//#region Artikelvarianten
	/**
	 * Initialisiert das Formular für Variantendefinitionen basierend auf den Eigenschaften des Artikels, falls es sich um eine Variantenbasis handelt.
	 * @param p_article Das Artikelobjekt, das die Informationen für die Variantendefinition enthält.
	 */
	private initVariantDefForm(p_article: ArticleDetailsByOid.Result): void {
		if (p_article?.isVariantBase) {
			// Initialisiert das Formular für Variantendefinitionen
			this.m_variantDefForm = this.m_formBuilder.group({});

			// Fügt jedem Attribut des Artikels ein Steuerelement zum Formular hinzu
			p_article?.variantDef?.attributes?.forEach((attr: ArticleDetailsByOid.Attribute) => {
				this.m_variantDefForm.addControl(attr.clsAttributeOid, new FormControl(null, attr.isMandatory ? Validators.required : null))
			});

			// Überwacht Änderungen im Formular und ruft die entsprechende Aktion auf
			this.m_variantDefForm.valueChanges.subscribe(c => this.onVariantClsAttributesSubmit());
			// Setzt die Attribut-Werte-Abbildung für alle verfügbaren Werte eines Attributes
			this.m_variantClsAttributeMap = this.m_articleService.getClsAttributeValues(p_article);
		}
	}

	/**
	 * Wählt eine Variante aus
	 * @param p_variant Variante
	 */
	private selectVariant(p_variant: ArticleDetailsByOid.Variant): void {
		p_variant?.values?.forEach((attrValue: ArticleDetailsByOid.Value) => {
			this.getVariantControl(attrValue.attributeOid).patchValue(attrValue.formatted);
		});
	}

	private selectVariantByOid(p_variantOid: string): void {
		const variant: ArticleDetailsByOid.Variant = this.m_articleDetails?.variantDef?.variants.find(v => v.articleOid === p_variantOid);
		if (variant) {

			variant?.values?.forEach((attrValue: ArticleDetailsByOid.Value) => {
				this.getVariantControl(attrValue.attributeOid).patchValue(attrValue.formatted);
			});
		}
	}

	/**
	 * Überprüft, ob ein bestimmtes Attribut deaktiviert werden soll, basierend auf den verfügbaren Varianten und den ausgewählten Attributwerten.
	 * @param p_clsAttr Die OID des Attributs, das überprüft werden soll.
	 * @param p_clsAttrValue Der Wert des zu überprüfenden Attributs.
	 * @returns Gibt true zurück, wenn das Attribut deaktiviert werden soll, andernfalls false.
	 */
	public isDisabled(p_clsAttr: string, p_clsAttrValue: string): boolean {

		// const availableVariants = this.checkVariantAvailability();
		// return !availableVariants.some(variant =>
		// 	variant.values.some(attr => attr.attributeOid === p_clsAttr && attr.formatted === p_clsAttrValue)
		// );

		const availableVariants = this.m_articleDetails?.variantDef?.variants || [];
		const selectedValues = this.getSelectedValues().filter(val => val.formatted !== null);

		// Filtern der ausgewählten Werte, um das aktuell zu prüfende Attribut auszuschließen
		const valuesToCheck = selectedValues.filter(sel => sel.attributeOid !== p_clsAttr);
		// Filtern der verfügbaren Varianten, die den verbleibenden ausgewählten Werten entsprechen
		const matchingVariants = availableVariants.filter(variant =>
			valuesToCheck.every(sel =>
				variant.values?.some(val =>
					val.attributeOid === sel.attributeOid && val.formatted === sel.formatted
				)
			)
		);
		// Überprüfen, ob das Attribut in allen verbleibenden Varianten existiert und wenn nicht, ob es deaktiviert werden soll
		return matchingVariants.every(variant =>
			!variant.values?.some(val =>
				val.attributeOid === p_clsAttr && val.formatted === p_clsAttrValue
			)
		);
	}

	/**
	 * Ruft die ausgewählten Attributwerte aus dem Variantendefinitionsformular ab.
	 * @returns Eine Liste der ausgewählten Attributwerte
	 */
	private getSelectedValues(): ArticleDetailsByOid.Value[] {
		const selectedAttrValues: ArticleDetailsByOid.Value[] = [];
		for (const control in this.m_variantDefForm.controls)
			// if (this.m_variantDefForm.controls[control].value)
			selectedAttrValues.push({ attributeOid: control, formatted: this.getVariantControl(control).value });
		return selectedAttrValues;
	}

	/**
	 * Liefert ein FormControl anhand seines Identifiers des Variantendefinitionsformular zurück
	 * @param p_identifier Identifier - hier OID des Attributes
	 * @returns FormControl
	 */
	public getVariantControl(p_identifier: string): FormControl {
		return this.m_variantDefForm.get(p_identifier) as FormControl;
	}

	/**
	 * Erstellt eine Matrix, die die Varianten basiered auf den Attributwerten darstellt.
	 * @returns Eine zweidimensionale Matrix, in der jede Zeile eine Variante repräsentiert und die Attributwerte als Zeilenwerte enthalten sind.
	 */
	private createVariantMatrix(): string[][] {
		const matrix: string[][] = [];
		const variants = this.m_articleDetails?.variantDef?.variants || [];
		const size = this.m_articleDetails?.variantDef?.attributes?.length;
		for (const variant of variants) {
			const row: string[] = [variant.articleOid]; // OID der Variante als erstes Element
			for (let i = 0; i < size; i++) {
				row.push(variant.values[i]?.formatted || null);
			}
			matrix.push(row);
		}
		return matrix;
	}


	/**
	 * Ermittelt die passende Variante basierend auf den ausgewählten Attributwerten und speichert die Varianten-OID zwischen, wenn eine passende Variante gefunden wird.
	 */
	private onVariantClsAttributesSubmit(): void {
		const selectedValues = this.getSelectedValues().map(val => val.formatted); // Konvertierung zu einem Array von formatierten Werten
		const matrix = this.createVariantMatrix();

		// Durchlaufen der Matrix, um die passende Variante zu finden
		for (let i = 0; i < matrix.length; i++) {
			const row = matrix[i];
			let match = true;

			// Überprüfen, ob alle ausgewählten Werte außer der OID in der aktuellen Zeile vorkommen
			for (let j = 1; j < row.length; j++) {
				if (!selectedValues.includes(row[j])) {
					match = false;
					break;
				}
			}

			// Wenn alle ausgewählten Werte in der aktuellen Zeile vorkommen, Variante gefunden
			if (match) {
				this.m_variantArticleOid = row[0]; // OID der Variante
				break;
			}
		}
	}
	//#endregion

	/**
	 * Ruft die aktuelle Konfigurations OID sowie eventielle Params ab.
	 */
	public ngOnInit(): void {
		this.m_selectedConfigOid = this.m_configurationService.SelectedConfigOid;
		this.m_configurationService.getConfiguration().pipe(takeUntil(this.m_subscriptionsSubject)).subscribe({
			next: (config) => {
				this.m_onlyAvailableStock = config?.onlyAvailableStock;
				this.m_showProcPrice = config?.showProcPrice;
				this.m_showStockLevel = config?.showStockLevel;
				this.m_showStoragePackaging = config?.showStoragePackaging;
				this.m_showSubPackaging = config?.showSubPackaging;
				this.m_showReservedStock = config?.showReservedStock;
				this.m_showBackOrder = config?.showBackOrder;
				this.m_showAvailableStock = config?.showAvailableStock;
				this.m_showReplacementTime = config?.showReplacementTime;
				this.m_showMinStock = config?.showMinStock;
				this.m_showSalesPrice = config?.showSalesPrice;
				this.m_showEAN = config?.showEAN;
				this.m_showCategory = config?.showCategory;
				this.m_showArticleGroup = config?.showArticleGroup;
				this.m_showDepartment = config?.showDepartment;
				// Key ist der Name der Property von m_articleDetails!
				this.m_showInfo.set('attrInfo1', config?.showInfo1);
				this.m_showInfo.set('attrInfo2', config?.showInfo2);
				this.m_showInfo.set('attrInfo3', config?.showInfo3);
				this.m_showInfo.set('attrInfo4', config?.showInfo4);
				this.m_showInfo.set('attrInfo5', config?.showInfo5);
				this.m_showInfo.set('attrInfo6', config?.showInfo6);
				this.m_showInfoKeys = Array.from(this.m_showInfo.keys());

			}
		})
		if (!this.isVariant)
			this.m_route.paramMap.pipe(takeUntil(this.m_subscriptionsSubject)).subscribe({
				next: (paramMap) => {
					if (paramMap.has(this.ARTICLE_OID)) {
						this.m_articleOid = paramMap.get(this.ARTICLE_OID);
						this.articleOid = this.m_articleOid;
					}
				},
			});
		this.m_route.fragment.subscribe({
			next: (fragment) => {
				if (fragment) {
					try {
						document.querySelector('#' + fragment).scrollIntoView();
					} catch (e) { }
				}
			}
		})
	}

	/**
	 * Die Subscriptions werden geschlossen.
	 */
	public ngOnDestroy(): void {
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	/**
	 * Gibt zurück, ob der Artikel über den
	 * "Auswählen"-Button auswählbar sein soll.
	 */
	public canArticleGetSelected(): boolean {
		let canArticleGetSelected = false;
		if (this.m_embedded) {
			if (this.m_onlyOrderableArticles === true) {
				canArticleGetSelected = this.m_articleService.canArticleGetAddedToBasket(this.m_onlyAvailableStock, this.m_articleDetails);
			} else {
				// alle Artikel anzeigen
				canArticleGetSelected = true;
			}
		}
		return canArticleGetSelected;
	}

	public getDeliveryDate(): Date {
		var date = new Date();
		if (this.m_articleDetails && this.m_articleDetails.replacementTime)
			date.setDate(date.getDate() + this.m_articleDetails.replacementTime);
		return this.m_articleDetails.replacementTime ? date : null;
	}

	/**
	 * Prüft, ob der Artikel in den Einkaufswagen
	 * gelegt werden kann.
	 */
	public canArticleGetAddedToBasket(): boolean {
		return !this.m_embedded && this.m_articleService.canArticleGetAddedToBasket(this.m_onlyAvailableStock, this.m_articleDetails);
	}
	public getMinimumAmountError(p_amount: number): string {
		return this.m_basketService.getMinimumAmountError(p_amount);
	}

	public getMaximumAmountError(p_amount: number): string {
		return this.m_basketService.getMaximumAmountError(p_amount);
	}
	/**
	 * Übergibt einen Artikel an die aufrufende Komponente.
	 * @param p_article Artikel der Artikelsuche
	 */
	public selectArticle_Click(p_articleOid: string): void {
		this.articleSelect.emit(p_articleOid);
	}

	/**
	 * Lädt alle Lobs für einen angegebenen Artikel.
	 * @param p_articleOid Artikel OID
	 */
	public getArticleLobOids(p_articleOid: string): void {
		const params = new ArticleLobOids.Params();
		params.article = p_articleOid;
		this.m_articleService.getArticleLobOids(params).pipe(
			takeUntil(this.m_subscriptionsSubject),
		).subscribe({
			next: (data) => {
				this.m_articleLobs = data;
			},
		});
	}

	/**
	 * Lädt einen Lob zum angegebenen Artikel.
	 * @param p_articleLobOid Artikel OID
	 */
	public getLobByOid(p_articleLobOid: string): string {
		const params = new LobByOid.Params();
		params.lob = p_articleLobOid;
		return this.m_articleService.getLobByOid(params);
	}

	/**
	 * Setzt die Artikeldetails zurück.
	 */
	public resetArticleDetails(): void {
		this.m_imageIndex == 0;
	}

	/**
	 * Die Artikelgalerie wird um eine Stelle nach links verschoben.
	 */
	public arrowLeft_Click(): void {
		if (this.m_imageIndex > 0) {
			this.m_imageIndex--;
		}
	}

	/**
	 * Die Artikelgalerie wird um eine Stelle nach rechts verschoben.
	 */
	public arrowRight_Click(): void {
		if (this.m_imageIndex < this.m_imageArray.length - 3) {
			this.m_imageIndex++;
		}
	}

	/**
	 * Ruft einen modalen Dialog mit dem aktiven Artikelbild auf.
	 */
	public openDialog_Click(): void {
		const dialogRef = this.m_dialog.open(ArticleImageDialog, {
			maxHeight: '100%',
			maxWidth: '100%',
			data: { oid: this.m_activeImage },
		});
	}

	/**
	 * Es wird zu der vorherigen Seite zurückgewechselt.
	 */
	public buttonBack_Click(): void {
		if (this.m_embedded) {
			this.exit.emit(true);
		} else {
			this.m_location.back();
		}
	}

	public articleCompare_Click(p_article: ArticleDetailsByOid.Result): void {
		this.m_articleService.addArticleCompare(p_article.oid);
	}

	/**
	 * Fügt einen Artikel auf Basis des angebenen Indexes in den Warenkorb. Im Fehlerfall gibt die aufgrufene Methode einen Fehlercode zurück.
	 */
	public async articleAddToBasket_Submit(): Promise<void> {
		await this.m_basketService.saveBasketItem(this.m_articleOid, this.m_selectedConfigOid, this.getControl(this.QTTY).value)
			.then(() => {
				this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
					data: {
						message: this.m_translation.globalBasketAddItemSuccess,
						type: 'success',
					},
					duration: 2000,
				});
			})
			.catch((error: Error) => {
				this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
					data: {
						message: error.message,
						type: 'danger',
					},
					duration: 2000,
				});
			});
	}

	/**
	 * Gibt ein FormControl basierend auf dessen Name zurück.
	 * @param p_name FormControlName
	 */
	public getControl(p_name: string): AbstractControl {
		return this.m_articleDetailForm.get(p_name);
	}
}

@Component({
	selector: 'article-image-dialog',
	templateUrl: 'article-image-dialog.html',
})
export class ArticleImageDialog {

	constructor(
		private m_articleService: ArticleService,
		public m_dialogRef: MatDialogRef<ArticleImageDialog>,
		@Inject(MAT_DIALOG_DATA) public m_data: ArticleImageDialogData,
	) { }

	/**
	 * Schliest den modalen Dialog.
	 */
	public backDialog_Click(): void {
		this.m_dialogRef.close(true);
	}

	/**
	 * Lädt einen Lob zum angegebenen Artikel.
	 * @param p_articleLobOid Artikel OID
	 */
	public getLobByOid(p_articleLobOid: string): string {
		const params = new LobByOid.Params();
		params.lob = p_articleLobOid;
		return this.m_articleService.getLobByOid(params);
	}
}
